import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, Users, Mail, Phone, Building, Clock, Trash2, RefreshCw, MessageSquare } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/leads?limit=100`);
      if (!response.ok) throw new Error('Error al cargar leads');
      const data = await response.json();
      setLeads(data.leads || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleExport = () => {
    window.open(`${API}/admin/leads/export`, '_blank');
  };

  const handleDelete = async (timestamp) => {
    if (!window.confirm('¿Estás seguro de eliminar este lead?')) return;
    
    try {
      const response = await fetch(`${API}/admin/leads/${encodeURIComponent(timestamp)}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Error al eliminar');
      fetchLeads();
    } catch (err) {
      alert('Error al eliminar el lead');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Users className="h-7 w-7 text-green-500" />
                Panel de Leads - Maquito
              </h1>
              <p className="text-slate-400 mt-1">
                {total} contacto{total !== 1 ? 's' : ''} registrado{total !== 1 ? 's' : ''}
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={fetchLeads}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Actualizar
              </button>
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors font-medium"
              >
                <Download className="h-4 w-4" />
                Exportar Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-400">{error}</p>
            <button 
              onClick={fetchLeads}
              className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg"
            >
              Reintentar
            </button>
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-20">
            <Users className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">No hay leads registrados aún</p>
            <p className="text-slate-500 mt-2">Los contactos aparecerán aquí cuando los usuarios compartan sus datos en el chat</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {leads.map((lead, index) => (
              <motion.div
                key={lead.timestamp || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 hover:border-green-500/50 transition-colors"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Nombre */}
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-green-500/20 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-green-500" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Nombre</p>
                        <p className="text-white font-medium">{lead.nombre || 'No especificado'}</p>
                      </div>
                    </div>

                    {/* Celular */}
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                        <Phone className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Celular</p>
                        <p className="text-white font-medium">{lead.celular || 'No especificado'}</p>
                      </div>
                    </div>

                    {/* Empresa */}
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                        <Building className="h-5 w-5 text-purple-500" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Empresa</p>
                        <p className="text-white font-medium">{lead.empresa || 'No especificado'}</p>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                        <Mail className="h-5 w-5 text-orange-500" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Email</p>
                        <p className="text-white font-medium truncate max-w-[200px]">{lead.email || 'No especificado'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Timestamp and Delete */}
                  <div className="flex items-center gap-4 lg:gap-6">
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <Clock className="h-4 w-4" />
                      <span>{lead.timestamp || 'Sin fecha'}</span>
                    </div>
                    <button
                      onClick={() => handleDelete(lead.timestamp)}
                      className="p-2 text-slate-500 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Eliminar lead"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Requerimiento del usuario */}
                {lead.requerimiento && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="flex items-start gap-3">
                      <div className="h-8 w-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MessageSquare className="h-4 w-4 text-green-500" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Requerimiento</p>
                        <p className="text-slate-200 text-sm">{lead.requerimiento}</p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
